import jsPDF from 'jspdf';
import { toast } from "sonner";
import { formatScoringRubricText } from "../../utils/analysisDisplay";

// Helper function to load image and convert to base64 (without Canvas processing)
const loadImageAsBase64 = (url) => {
    return fetch(url)
        .then(response => {
            if (!response.ok) {
                throw new Error(`Failed to fetch image: ${response.statusText}`);
            }
            return response.blob();
        })
        .then(blob => {
            return new Promise((resolve, reject) => {
                // Convert blob to base64 using FileReader
                const reader = new FileReader();
                reader.onloadend = () => {
                    // Get image dimensions using Image object
                    const img = new Image();
                    img.onload = () => {
                        resolve({
                            base64: reader.result,
                            width: img.width,
                            height: img.height
                        });
                    };
                    img.onerror = reject;
                    img.src = reader.result;
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        });
};

// Cache for logo image data
let logoCache = null;

// Helper function to add logo watermark to PDF page
const addLogoWatermark = async (doc) => {
    try {
        // Load logo if not cached
        if (!logoCache) {
            try {
                logoCache = await loadImageAsBase64('/june logo png.png');
            } catch (loadError) {
                console.error('Failed to load logo image:', loadError);
                return;
            }
        }
        
        if (!logoCache || !logoCache.base64) {
            console.warn('Logo cache is empty');
            return;
        }
        
        // Scale down by 10x while maintaining aspect ratio
        const pxToMm = 0.264583;
        const imgWidth = (logoCache.width * pxToMm) / 10;
        const imgHeight = (logoCache.height * pxToMm) / 10;
        
        // Position at top-left corner 
        const x = 5; 
        const y = 5; 
        
        // Add image at top-left corner with original color (no transformations)
        doc.addImage(
            logoCache.base64,
            'PNG',
            x,
            y,
            imgWidth,
            imgHeight
        );
    } catch (error) {
        console.error('Failed to add logo watermark:', error);
        // Continue without logo if image fails to load
    }
};

export const copyAllQuestionsHandler = async (editedQuestions, roleName, companyName) => {
    if (!editedQuestions || editedQuestions.length === 0) {
        toast.error("No questions to copy");
        return;
    }

    let questionsText = "";

    // Add role and company information if available
    if (roleName) {
        questionsText += `Role: ${roleName}\n`;
    }
    if (companyName) {
        questionsText += `Company: ${companyName}\n`;
    }
    if (roleName || companyName) {
        questionsText += "\n";
    }

    // Add questions
    questionsText += editedQuestions.map((q, index) => {
        return `Question ${index + 1}: ${q.question || ''}\nScoring Rubric:\n${formatScoringRubricText(q)}\n`;
    }).join('\n');

    // Add Powered by June watermark
    questionsText += '\nPowered by June';

    try {
        if (navigator.clipboard && window.isSecureContext) {
            // Modern clipboard API
            await navigator.clipboard.writeText(questionsText);
            toast.success("All interview questions copied to clipboard");
        } else {
            // Fallback for older browsers or non-secure contexts
            const textArea = document.createElement("textarea");
            textArea.value = questionsText;
            textArea.style.position = "fixed";
            textArea.style.left = "-999999px";
            textArea.style.top = "-999999px";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            try {
                document.execCommand('copy');
                toast.success("All interview questions copied to clipboard");
            } catch (err) {
                toast.error("Failed to copy questions. Please select and copy manually.");
            }

            document.body.removeChild(textArea);
        }
    } catch (error) {
        console.error("Error copying to clipboard:", error);
        toast.error("Failed to copy questions to clipboard");
    }
};

export const exportToPDFHandler = async (editedQuestions, roleName, companyName) => {
    if (!editedQuestions || editedQuestions.length === 0) {
        toast.error("No questions to export");
        return;
    }

    try {
        const doc = new jsPDF();
        const lineHeight = 10;
        let yPosition = 20;
        const margin = 20;
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;

        // Add logo watermark to first page
        await addLogoWatermark(doc);

        // Header
        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        doc.text("Interview Questions", margin, yPosition);
        yPosition += lineHeight * 2;

        // Add role and company information if available
        if (roleName || companyName) {
            doc.setFontSize(12);
            doc.setFont("helvetica", "bold");

            if (roleName) {
                doc.text(`Role: ${roleName}`, margin, yPosition);
                yPosition += lineHeight;
            }
            if (companyName) {
                doc.text(`Company: ${companyName}`, margin, yPosition);
                yPosition += lineHeight;
            }
            yPosition += lineHeight; // Add extra space
        }

        // Questions
        doc.setFontSize(12);

        for (let index = 0; index < editedQuestions.length; index++) {
            const question = editedQuestions[index];
            // Check if we need a new page
            if (yPosition > pageHeight - 60) {
                doc.addPage();
                await addLogoWatermark(doc);
                yPosition = 20;
            }

            // Question number and text
            doc.setFont("helvetica", "bold");
            doc.text(`Question ${index + 1}:`, margin, yPosition);
            yPosition += lineHeight;

            doc.setFont("helvetica", "normal");
            const questionLines = doc.splitTextToSize(question.question || '', pageWidth - margin * 2);
            doc.text(questionLines, margin, yPosition);
            yPosition += questionLines.length * lineHeight + lineHeight;

            // Scoring Rubric
            doc.setFont("helvetica", "bold");
            doc.text("Scoring Rubric:", margin, yPosition);
            yPosition += lineHeight;

            doc.setFont("helvetica", "normal");
            const answerLines = doc.splitTextToSize(formatScoringRubricText(question), pageWidth - margin * 2);
            doc.text(answerLines, margin, yPosition);
            yPosition += answerLines.length * lineHeight + lineHeight * 2;

            // Add separator line between questions (except for the last one)
            if (index < editedQuestions.length - 1) {
                doc.setDrawColor(200, 200, 200);
                doc.line(margin, yPosition, pageWidth - margin, yPosition);
                yPosition += lineHeight;
            }
        }

        // Footer
        doc.setFontSize(10);
        doc.setFont("helvetica", "italic");
        doc.text(`Total Questions: ${editedQuestions.length}`, margin, pageHeight - 20);

        // Save the PDF
        const fileName = `interview-questions-${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        toast.success("Interview questions exported to PDF");
    } catch (error) {
        console.error("Error generating PDF:", error);
        toast.error("Failed to export PDF. Please try again.");
    }
};
